/**
 * This component contains data of the last 48 hours
 * and displays around 90 points
 */
import React from 'react';
import PropTypes from 'prop-types';
import "./HistoryChart.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import moment from 'moment';
import { DATE_TIME_FORMAT } from 'constants/config';
import { TEXT_FLOOD_GATE_OPENING_LEVEL, TEXT_INNER_WATER_LEVEL, TEXT_OUTER_WATER_LEVEL } from 'constants/message';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
const N = 60;

Tooltip.positioners.MouseTooltip = function (elements, eventPosition) {
  // A reference to the tooltip model
  return {
    x: eventPosition.x,
    y: eventPosition.y
  };
};
const INIT_OPTIONS = {
  responsive: true,
  bezierCurve: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  stacked: false,
  plugins: {
    legend: {
    },
    title: {
      display: true,
      text: '水門データ履歴',
    },
    tooltip: {
      position: 'MouseTooltip',
      callbacks: {
        label: (data) => {
          const dataset = data.dataset;
          const parsedData = data.parsed?.y;
          return dataset.label + ': ' + parsedData.toFixed(2);
        },
        title: (data) => {
          return ""
        }
      }
    }
  },
  scales: {
    y1: {
      type: 'linear',
      display: true,
      position: 'left',
      title: {
        text: '水位（m）',
        display: true,
      }
    },
    y2: {
      type: 'linear',
      display: true,
      position: 'right',
      grid: {
        drawOnChartArea: false,
      },
      title: {
        text: '開度（°)',
        display: true,
      }
    }
  },
  elements: {
    point: {
      hitRadius: 0,
      hoverRadius: 3
    }
  }
};
const INIT_DATASETS = [
  {
    label: TEXT_OUTER_WATER_LEVEL,
    data: [],
    borderColor: 'rgb(51, 102, 255)',
    backgroundColor: 'rgba(51, 102, 255, 0.5)',
    yAxisID: 'y1',
    // borderDash: [10, 10],
    showLine: true,
  },
  {
    label: TEXT_INNER_WATER_LEVEL,
    data: [],
    borderColor: 'rgb(50, 168, 82)',
    backgroundColor: 'rgba(51, 168, 82, 0.5)',
    yAxisID: 'y1',
    // borderDash: [4, 10, 10, 10],
    showLine: true,
  },
  {
    label: TEXT_FLOOD_GATE_OPENING_LEVEL,
    data: [],
    borderColor: 'rgb(160, 160, 160)',
    backgroundColor: 'rgba(160, 160, 160, 0.5)',
    yAxisID: 'y2',
    showLine: true,
  },
  {
    label: '川表水位閾値',
    data: [],
    borderColor: 'rgb(51, 102, 255)',
    backgroundColor: 'rgba(51, 102, 255, 0.5)',
    yAxisID: 'y1',
    borderDash: [4, 10, 10, 10],
    showLine: true,
  },
  {
    label: '川裏水位閾値',
    data: [],
    borderColor: 'rgb(50, 168, 82)',
    backgroundColor: 'rgba(50, 168, 82, 0.5)',
    yAxisID: 'y1',
    borderDash: [4, 10, 10, 10],
    showLine: true,
  },
  {
    label: '水門開度閾値',
    data: [],
    borderColor: 'rgb(160, 160, 160)',
    backgroundColor: 'rgba(160, 160, 160, 0.5)',
    yAxisID: 'y2',
    borderDash: [4, 10, 10, 10],
    showLine: true
  }
];
const propTypes = {
  sensor: PropTypes.object,
  historyItems: PropTypes.array
};
const defaultProps = {
  sensor: {},
  historyItems: []
};
const HistoryChart = (props) => {
  /*** States and Variables ***/
  const txsRef = React.useRef([]);
  const outerWaterLevelsRef = React.useRef([]);
  const innerWaterLevelsRef = React.useRef([]);
  const gateOpeningLevelsRef = React.useRef([]);
  const [displayData, setDisplayData] = React.useState({
    labels: [],
    datasets: []
  });
  const [options, setOptions] = React.useState(INIT_OPTIONS);
  const [displayTime, setDisplayTime] = React.useState(48);
  /*** Processing ***/
  React.useEffect(() => {
    txsRef.current = [];
    outerWaterLevelsRef.current = [];
    innerWaterLevelsRef.current = [];
    gateOpeningLevelsRef.current = [];
    let minLeftY = Infinity, maxLeftY = -Infinity, minRightY = Infinity, maxRightY = -Infinity;
    for (let i = props.historyItems.length - 1; i >= 0; i--) {
      const item = props.historyItems[i];
      const tx = (new Date(item.tx)).getTime();
      txsRef.current.push(tx);
      outerWaterLevelsRef.current.push({ x: tx, y: item.data.outerWaterLevel });
      innerWaterLevelsRef.current.push({ x: tx, y: item.data.innerWaterLevel });
      gateOpeningLevelsRef.current.push({ x: tx, y: item.data.gateOpeningLevel });
      minLeftY = item.data.outerWaterLevel < minLeftY ? item.data.outerWaterLevel : minLeftY;
      minLeftY = item.data.innerWaterLevel < minLeftY ? item.data.innerWaterLevel : minLeftY;
      maxLeftY = item.data.outerWaterLevel > maxLeftY ? item.data.outerWaterLevel : maxLeftY;
      maxLeftY = item.data.innerWaterLevel > maxLeftY ? item.data.innerWaterLevel : maxLeftY;
      minRightY = item.data.gateOpeningLevel < minRightY ? item.data.gateOpeningLevel : minRightY;
      maxRightY = item.data.gateOpeningLevel > maxRightY ? item.data.gateOpeningLevel : maxRightY;
    }
    minLeftY = props.sensor?.data?.Wa < minLeftY ? props.sensor.data.Wa : minLeftY;
    maxLeftY = props.sensor?.data?.Wa > maxLeftY ? props.sensor.data.Wa : maxLeftY;
    minLeftY = props.sensor?.data?.Wb < minLeftY ? props.sensor.data.Wb : minLeftY;
    maxLeftY = props.sensor?.data?.Wb > maxLeftY ? props.sensor.data.Wb : maxLeftY;
    minRightY = props.sensor?.data?.Wc < minRightY ? props.sensor.data.Wc : minRightY;
    maxRightY = props.sensor?.data?.Wc > maxRightY ? props.sensor.data.Wc : maxRightY;
    let delta = (maxLeftY - minLeftY) / 10;
    INIT_OPTIONS.scales.y1.min = props.sensor?.data?.minWaterLevel || props.sensor.data.minWaterLevel === 0 ? props.sensor.data.minWaterLevel : (minLeftY - delta);
    INIT_OPTIONS.scales.y1.max = props.sensor?.data?.maxWaterLevel || props.sensor.data.maxWaterLevel === 0 ? props.sensor.data.maxWaterLevel : (maxLeftY + delta);
    delta = (maxRightY - minRightY) / 10;
    INIT_OPTIONS.scales.y2.min = props.sensor?.data?.minOpeningLevel || props.sensor.data.minOpeningLevel === 0 ? props.sensor.data.minOpeningLevel : (minRightY - delta);
    INIT_OPTIONS.scales.y2.max = props.sensor?.data?.maxOpeningLevel || props.sensor.data.maxOpeningLevel === 0 ? props.sensor.data.maxOpeningLevel : (maxRightY + delta);
    INIT_OPTIONS.plugins.tooltip.callbacks.label = (data) => {
      const datasetLabel = data.dataset.label;
      if (datasetLabel === TEXT_OUTER_WATER_LEVEL || datasetLabel === TEXT_INNER_WATER_LEVEL || datasetLabel === TEXT_FLOOD_GATE_OPENING_LEVEL) {
        const x = data.parsed?.x;
        let pos = txsRef.current.findIndex(tx => tx >= x);
        if (pos < 0) pos = txsRef.current.length - 1;
        if (datasetLabel === TEXT_OUTER_WATER_LEVEL) return datasetLabel + ': ' + outerWaterLevelsRef.current[pos].y.toFixed(2);
        if (datasetLabel === TEXT_INNER_WATER_LEVEL) return datasetLabel + ': ' + innerWaterLevelsRef.current[pos].y.toFixed(2);
        return datasetLabel + ': ' + gateOpeningLevelsRef.current[pos].y.toFixed(1);
      }
      return datasetLabel + ': ' + data.parsed.y.toFixed(3);
    }
    resetData(48);
  }, [props.historyItems, props.sensor.id]);

  const resetData = (displayTime) => {
    // reset dataset
    const datasets = Object.assign([], INIT_DATASETS);
    datasets[0].data = [];
    datasets[1].data = [];
    datasets[2].data = [];
    const txs = txsRef.current;
    const minTx = txs[txs.length - 1] - displayTime * 3600000;
    const step = (txs[txs.length - 1] - minTx) / N + 1;
    let lastPos = -1;
    const outerWaterLevels = outerWaterLevelsRef.current;
    const innerWaterLevels = innerWaterLevelsRef.current;
    const gateOpeningLevels = gateOpeningLevelsRef.current;
    for (let i = 0; i < N; i = i + 1) {
      // find first tx which is >= txs[0] + step * i
      const pos = txs.findIndex((tx) => (tx >= minTx + step * i) && (tx >= minTx));
      if (pos === lastPos) {
        continue;
      }
      else {
        // find local maximum, minimum points
        if (lastPos >= 0) {
          let tv1 = Math.max(outerWaterLevels[lastPos].y, outerWaterLevels[pos].y), t1;
          let tv2 = Math.max(innerWaterLevels[lastPos].y, innerWaterLevels[pos].y), t2;
          let tv3 = Math.max(gateOpeningLevels[lastPos].y, gateOpeningLevels[pos].y), t3;
          for (let ti = lastPos + 1; ti < pos; ti++) {
            if (outerWaterLevels[ti].y > tv1) {
              tv1 = outerWaterLevels[ti].y;
              t1 = ti;
            }
            if (innerWaterLevels[ti].y > tv2) {
              tv2 = innerWaterLevels[ti].y;
              t2 = ti;
            }
            if (gateOpeningLevels[ti].y > tv3) {
              tv3 = gateOpeningLevels[ti].y;
              t3 = ti;
            }
          }
          if (t1 >= 0) datasets[0].data.push(outerWaterLevels[t1]);
          if (t2 >= 0) datasets[1].data.push(innerWaterLevels[t2]);
          if (t3 >= 0) datasets[2].data.push(gateOpeningLevels[t3]);
        }
        // update last pos
        lastPos = pos;
      }
      if (pos >= 0) {
        datasets[0].data.push(outerWaterLevels[pos]);
        datasets[1].data.push(innerWaterLevels[pos]);
        datasets[2].data.push(gateOpeningLevels[pos]);
      }
    }
    datasets[3].data = [
      { x: minTx, y: props.sensor?.data?.Wa },
      { x: txs[txs.length - 1], y: props.sensor?.data?.Wa }
    ]
    datasets[4].data = [
      { x: minTx, y: props.sensor?.data?.Wb },
      { x: txs[txs.length - 1], y: props.sensor?.data?.Wb }
    ]
    datasets[5].data = [
      { x: minTx, y: props.sensor?.data?.Wc },
      { x: txs[txs.length - 1], y: props.sensor?.data?.Wc }
    ]
    setDisplayData({ datasets });
    // reset option
    const newOptions = Object.assign({}, INIT_OPTIONS);
    newOptions.scales.x = {
      ticks: {
        callback: (val) => {
          const time = new Date(val);
          return "'" + moment(time).format(DATE_TIME_FORMAT).substring(2);
        }
      }
    }
    newOptions.plugins.tooltip.callbacks.title = (data) => {
      const x = data[0] && data[0].parsed?.x;
      const time = new Date(x);
      return "'" + moment(time).format(DATE_TIME_FORMAT).substring(2);
    }
    newOptions.scales.y2.title.text = `開度（${props.sensor?.data?.gateOpeningUnit})`
    setOptions(newOptions);
  }
  /*** Sub Components ***/
  /*** Event Handlers ***/
  /*** Main Render ***/
  return <>
    <div className='history_chart_container'>
      <select
        value={displayTime}
        onChange={(e) => { setDisplayTime(e.target.value); resetData(e.target.value) }}
        className='history_chart_time_select'>
        <option value={72}>72時間</option>
        <option value={48}>48時間</option>
        <option value={24}>24時間</option>
        <option value={12}>12時間</option>
        <option value={6}>06時間</option>
        <option value={3}>03時間</option>
        <option value={1}>01時間</option>
      </select>
    </div>
    <Scatter options={options} data={displayData} />
  </>;
}
HistoryChart.propTypes = propTypes;
HistoryChart.defaultProps = defaultProps;
export default HistoryChart;